import {
  Paper,
  Box,
  GridLegacy as Grid,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Button,
  Stack,
  Tooltip,
  Chip,
  Modal,
  Fade,
  TextField,
  IconButton,
} from '@mui/material';
import {
  textDisabledTablet,
  buttonDataDetail,
  configButtonStylesContained,
} from '@old/styles/generic-styles';
import CircularProgress from '@mui/material/CircularProgress';
import { formatDate } from '@old/utils/helper';
import { useToggle } from '@old/hooks/core';
import { ObservationModal } from './modal/observation-modal';
import imgIcon from '@old/assets/svg/img.svg';
import imgDisabledIcon from '@old/assets/svg/imgDisabled.svg';
import ObservationIcon from '@old/assets/svg/SiloBagPage/view/observations.svg';
import ObservationDisabledIcon from '@old/assets/svg/SiloBagPage/view/observation-disabled.svg';
import EditIcon from '@old/assets/svg/SiloBagPage/view/edit.svg';
import GreyEditIcon from '@old/assets/svg/SiloBagPage/view/edit-disabled.svg';
import ReloadIcon from '@old/assets/svg/SiloBagPage/view/reload.svg';
import { FileModal } from './modal/file-modal';
import FileIcon from '@old/assets/svg/SiloBagPage/view/fileView.svg';
import FileDisabledIcon from '@old/assets/svg/SiloBagPage/view/fileDisabled.svg';
import { useNavigate } from 'react-router';
import { PATH } from '@old/data/core/paths';
import copyIcon from '@old/assets/svg/copy.svg';
import ChevronDown from '@old/assets/svg/chevron-down.svg';
import { removeFileService } from '@old/services/domain/base/silobag/file/file-deletion';
import { useService } from '@old/hooks/use-service';
import { ToastAction } from '@old/ui/toast/toast-action';
import i18n from '../../../../libs/language';
import NewReleaseIcon from '@old/assets/svg/SiloBagPage/new_releases.svg';
import warningIcon from '@old/assets/svg/warning.svg';
import { PropsWithChildren, useEffect, useState } from 'react';
import { findWorstTAS } from '@old/utils/helper';
import { ModalContent } from './edit-silobag-modal';
import { silobagDataDetailService } from '@old/services/domain/base/silobag/base/silobag-data-detail';
import type {
  SatCheck,
  SatEstimatedWeightKg,
} from '@old/services/domain/base/silobag/base/silobag-data-detail.types';
import MapIcon from '@old/assets/svg/SiloBagPage/view/map.svg';
import DisabledMapIcon from '@old/assets/svg/SiloBagPage/view/map-disabled.svg';
import type { Dayjs } from 'dayjs';
import { updateSilobagService } from '@old/services/domain/base/silobag/base/silobag-edition';
import { ImagesGalery } from '../modal/images-galery/images-galery';
import { silobagImagesService } from '@old/services/domain/base/silobag/images/silobag-images';
import HelpIcon from '@old/assets/svg/helpIcon.svg';

type SilobagDetailParams = Parameters<typeof silobagDataDetailService>[0];
type SummariDetailSb = Awaited<ReturnType<typeof silobagDataDetailService>>['summary'];
type SatViewSummary = Awaited<ReturnType<typeof silobagDataDetailService>>['summary']['sat_view'];

type detailProps = {
  data: SummariDetailSb;
  isLoading?: boolean;
  isClosed?: boolean;
  getData: (query: SilobagDetailParams) => void;
  siloListId?: string[];
  siloID: string;
};

/**
 * TIC-3909: el detalle mezcla numéricos como string (Prisma `numeric`), number y null.
 * Devuelve `null` en vez de `NaN` para que la UI nunca imprima "NaN".
 */
function toFiniteNumber(value: unknown): number | null {
  if (value === null || value === undefined || value === '') return null;
  const parsed = typeof value === 'number' ? value : parseFloat(String(value));
  return Number.isFinite(parsed) ? parsed : null;
}

/** TIC-3909: metros con un decimal. */
function formatMeters(value: number | null): string | null {
  return value === null ? null : `${value.toFixed(1)} m`;
}

/** TIC-3909: kilos con el separador de miles del locale, igual que el peso embolsado. */
function formatKilograms(value: number | null): string | null {
  if (value === null) return null;
  const delimited = i18n.numberToDelimited(Math.round(value), {
    delimiter: i18n.t('config.delimiter'),
  });
  return `${delimited} kg`;
}

/**
 * TIC-3909: los `*_diff_pct` de `sat_measurements` ya vienen **en porcentaje y con signo**
 * (positivo = el dato declarado es mayor que lo verificado), no en fracción: el backend los calcula
 * como `(declarado - referencia) / referencia x 100`. El front solo toma el valor absoluto para el
 * texto: la celda ya muestra el declarado y la referencia medida al lado, así que el signo no
 * agrega información.
 */
function toAbsolutePct(value: unknown): number | null {
  const parsed = toFiniteNumber(value);
  return parsed === null ? null : Math.abs(parsed);
}

/**
 * TIC-3909: desvío de la fecha de embolsado (`sat_measurements.bagging_date_diff_days`).
 * El campo viene **con signo**: negativo = la fecha declarada es anterior al rango verificado,
 * positivo = posterior, 0 = dentro del rango. Acá se muestra el valor absoluto y la dirección por
 * separado. Devuelve `null` cuando el backend no manda el dato (versión anterior a este campo) o
 * cuando no hay dirección que informar, así el tooltip queda como estaba y nunca imprime "null días".
 * Las claves no usan la pluralización de i18n-js, por eso hay una variante singular y una plural.
 */
function formatBaggingDateDiff(value: unknown): string | null {
  const parsed = toFiniteNumber(value);
  if (parsed === null) return null;
  const days = Math.abs(Math.round(parsed));
  if (days === 0) return null;
  const direction = parsed < 0 ? 'before' : 'after';
  const plural = days === 1 ? 'one' : 'other';
  return i18n.t(`app.silobag_data.bagging_date_diff_${direction}_${plural}`, { days });
}

/** TIC-3909: banda estimada "min – max" ya formateada, o `null` si falta algún extremo. */
function formatWeightBand(min: number | null, max: number | null): string | null {
  if (min === null || max === null) return null;
  const delimiter = i18n.t('config.delimiter');
  const from = i18n.numberToDelimited(Math.round(min), { delimiter });
  const to = i18n.numberToDelimited(Math.round(max), { delimiter });
  return `${from} – ${to} kg`;
}

type SatFieldProps = {
  label: string;
  /** Valor ya formateado; `null` se muestra como "no disponible". */
  value: string | null;
  /** Texto chico debajo del valor: fecha de la imagen, banda estimada, etc. */
  caption?: string | null;
  /** Ícono de verificación a la derecha del valor: solo en los datos declarados. */
  check?: SatCheck | null;
  checkTooltip?: string;
  /** Trazabilidad (id de imagen SiloSat): va al tooltip del valor, nunca como dato principal. */
  traceTooltip?: string;
};

/** TIC-3909: celda de la sección de medidas verificadas (valor arriba, etiqueta abajo). */
function SatField({ label, value, caption, check, checkTooltip, traceTooltip }: SatFieldProps) {
  const displayValue = value ?? i18n.t('app.silobag_data.not_available');
  const valueNode = (
    <Typography color={'#49454F'} fontWeight={600} display={'block'} fontSize={{ xs: '15px', md: '18px' }}>
      {displayValue}
    </Typography>
  );

  return (
    <Stack>
      <Stack direction="row" spacing={1} alignItems="center">
        {traceTooltip ? (
          <Tooltip title={traceTooltip}>
            <Box component="span">{valueNode}</Box>
          </Tooltip>
        ) : (
          valueNode
        )}
        {check === 'OK' && (
          <Tooltip title={checkTooltip ?? ''}>
            <Box component="span" display="flex">
              <img src={NewReleaseIcon} alt="release-icon" />
            </Box>
          </Tooltip>
        )}
        {check === 'INCONSISTENT' && (
          <Tooltip title={checkTooltip ?? ''}>
            <Box component="span" display="flex">
              <img src={warningIcon} alt="warning-icon" />
            </Box>
          </Tooltip>
        )}
      </Stack>
      <Typography sx={textDisabledTablet} style={{ fontSize: '13px' }}>
        {label}
      </Typography>
      {caption && (
        <Typography sx={textDisabledTablet} style={{ fontSize: '12px', opacity: 0.85 }}>
          {caption}
        </Typography>
      )}
    </Stack>
  );
}

export function DataSection({ data, isLoading, isClosed, getData, siloListId, siloID }: detailProps) {
  const [modalObservation, setModalObservation] = useToggle(false);
  const [modalImage, setModalImage] = useToggle(false);
  const [modalFiles, setModalFiles] = useToggle(false);
  const navigate = useNavigate();
  const [openModal, setOpenModal] = useToggle(false);
  const [sizeModal, setSizeModal] = useToggle(false);
  const [selectedDate, setSelectedDate] = useState<Date | Dayjs | null>(null);
  const [selectSize, SetSelectSize] = useState('');
  const [speciesName, setSpeciesName] = useState('');

  const isDisabledSB = data?.silobag_data_status_id === 1 || !data.active;

  const handleModalObervationOpen = () => {
    setModalObservation(true);
  };
  //datos con destructuring extensos
  const created_at = formatDate(data?.created_at);
  const specie_type = data?.silobag_species_types;
  const product_type = data?.silobag_product_types.silobag_product_types_translations[0].description;

  const countrysideName = data?.lots?.countrysides.name;

  const mainImage = data?.main_image;
  const initImage = data?.init_image;
  const endImage = data?.end_image;
  const handleModalImageOpen = () => {
    setModalImage(true);
  };

  const { trigger: editSilobag } = useService(updateSilobagService, {
    onSuccess: () => {
      getData({ query: siloID });
    },
  });

  // ADD GALLERY IMAGES SERVICE HERE
  const { summary, isLoading: isSbImgLoading } = useService(silobagImagesService, {
    fetchOnMount: [{ query: siloID as string }],
  });
  const totalImages = summary?.response.reduce((count, item) => count + item.images.length, 0);

  const silobag_actions = data?.silobags_actions;
  const imagesSilo = [
    {
      url: initImage,
      label: initImage !== null ? `Imagen inicial` : '',
      init_azimuth: data?.init_azimuth,
      init_timestamp: data?.init_timestamp,
      init_lat: data?.init_lat,
      init_lon: data?.init_lon,
      init_accuracy: data?.init_accuracy,
    },
    {
      url: mainImage,
      label: mainImage !== null ? `Imagen principal` : '',
      main_azimuth: data?.main_azimuth,
      main_timestamp: data?.main_timestamp,
      main_lat: data?.main_lat,
      main_lon: data?.main_lon,
      main_accuracy: data?.main_accuracy,
    },
    {
      url: endImage,
      label: endImage !== null ? `Imagen final` : '',
      end_azimuth: data?.end_azimuth,
      end_timestamp: data?.end_timestamp,
      end_lat: data?.end_lat,
      end_lon: data?.end_lon,
      end_accuracy: data?.end_accuracy,
    },
  ].filter((image) => image.url != null);

  if (mainImage !== null || initImage !== null || endImage !== null) {
    if (silobag_actions?.length > 0) {
      silobag_actions.map((action) => {
        if (action?.image !== null) {
          imagesSilo.push({
            url: action?.image,
            label: 'Fotos Extras',
            init_azimuth: action?.azimuth,
            init_timestamp: action?.timestamp,
            init_lat: action?.lat,
            init_lon: action?.lon,
            init_accuracy: action?.accuracy,
          });
        }
      });
    }
  }
  const files = data?.files;

  const handleModalFilesOpen = () => {
    setModalFiles(true);
  };

  const copyId = (id: string) => navigator.clipboard.writeText(id);
  const [idCopied, setIdCopied] = useState(false);

  const originLots = data?.silobags_origin_lots?.map((originLot) => originLot);

  const { trigger: deleteFile, isSuccess } = useService(removeFileService, {
    onSuccess: () => {
      setTimeout(() => {
        getData({ query: siloID });
      }, 1000);
    },
  });

  const dateClient = data?.bagging_date;

  // Conserva los cambios de la rama DEV
  const validSatViews =
    (data &&
      data.sat_view?.filter(
        (view: SatViewSummary[0]) => view.status_id === 1 && view.sat_view_providers.id === 3
      )) ||
    [];
  validSatViews.sort((a, b) => {
    const dateA = new Date(a.timestamp ?? '').getTime();
    const dateB = new Date(b.timestamp ?? '').getTime();
    return dateA - dateB;
  });
  const firstValidSatView = validSatViews[0];
  const dateToShow = firstValidSatView?.timestamp ?? data?.bagging_date ?? '';
  const dateToShowDate = new Date(dateToShow);
  const now = new Date();
  const oneDay = 24 * 60 * 60 * 1000;
  const daysToShow = dateToShow
    ? Math.round(Math.abs((dateToShowDate.getTime() - now.getTime()) / oneDay))
    : '-';
  //TODO Revisar esta logica con el 0 como valor por defecto
  const sizeClient = data?.size ? parseFloat(data?.size) : 0;

  const isGuest: boolean = data?.is_guest;
  //Logica para mostrar el TAS en el card principal
  const worstTas = findWorstTAS(data);

  const tasDecorators: Record<string, string> = {
    '<1': '<1 mes',
    '1-6': '1-6 meses',
    '>6': '>6 meses',
    NA: '―',
  };

  const TAS = tasDecorators[worstTas] || '―';
  const handleCloseModal = () => setOpenModal(false);

  const handleUpdateDate = () => {
    if (data && selectedDate) {
      const silobagToUpdate = {
        id: data.id,
        bagging_date: selectedDate as Date,
      };
      editSilobag({ query: siloID, body: silobagToUpdate });
    }
    setOpenModal(false);
  };

  const handleClose = () => {
    setOpenModal(false);
  };
  const handleCloseSizeModal = () => {
    setSizeModal(false);
  };
  const handleSizeChange = () => {
    if (data && selectSize) {
      const silobagToUpdate = {
        id: data.id,
        size: selectSize,
      };
      editSilobag({ query: siloID, body: silobagToUpdate });
    }
    setSizeModal(false);
  };

  const { image_url, name, silobag_species_types_translations } = specie_type || {};
  useEffect(() => {
    const newSpeciesName =
      i18n.locale === 'pt' && silobag_species_types_translations?.length
        ? silobag_species_types_translations[0].description
        : name;
    setSpeciesName(newSpeciesName);
  }, [i18n.locale, silobag_species_types_translations, name]);

  // --- TIC-3909: medidas verificadas por SiloSat ---
  // Todo sale del bloque `sat_measurements` que calcula el backend (largos medidos, pesos estimados
  // por especie/diámetro, rango de embolsado y los checks de las reglas de inconsistencias v2).
  // El front no recalcula tolerancias, coeficientes ni desvíos, y tampoco vuelve a leer
  // `sat_view_events` / `v_silobags_bagging_date_check`: eran cálculos aproximados que no coincidían
  // con la regla. Si el backend no devuelve el bloque, no hay check y no se muestra ícono.
  const sat = data?.sat_measurements ?? null;

  const satInitialLength = toFiniteNumber(sat?.initial_length_m);
  const satCurrentLength = toFiniteNumber(sat?.current_length_m);
  const satFactor = toFiniteNumber(sat?.factor);
  const satDiameterFt = toFiniteNumber(sat?.diameter_ft);
  // Solo tiene sentido mostrar el diámetro cuando corrige el peso estimado (silobolsas de 12 pies).
  const showSatDiameter = satFactor !== null && satFactor !== 1 && satDiameterFt !== null;

  // Los checks (largo, peso embolsado, stock actual y fecha) y sus desvíos salen del backend, de los
  // mismos predicados que evalúan las reglas. Un backend anterior a TIC-3909 no manda ni el check ni
  // el desvío: queda `NOT_AVAILABLE` (sin ícono, nunca warning) y el dato declarado se muestra igual.
  const satLengthCheck = sat?.length_check ?? 'NOT_AVAILABLE';
  const satInitialWeightCheck = sat?.initial_weight_check ?? 'NOT_AVAILABLE';
  const satCurrentWeightCheck = sat?.current_weight_check ?? 'NOT_AVAILABLE';
  const satLengthDiffPct = toAbsolutePct(sat?.length_diff_pct);
  const satInitialWeightDiffPct = toAbsolutePct(sat?.initial_weight_diff_pct);
  const satCurrentWeightDiffPct = toAbsolutePct(sat?.current_weight_diff_pct);

  const declaredWeight = toFiniteNumber(data?.input_weight);
  const remainingStock = toFiniteNumber(data?.remaining_stock);

  // Rango de embolsado verificado y su check: solo del bloque `sat_measurements`, que lo arma con
  // las dos imágenes del evento de embolsado (tipo 1). El detalle trae `sat_view_events` sin filtrar
  // por tipo ni ordenar, así que leer `[0]` podía devolver un evento de extracción.
  const satBaggingFrom = sat?.verified_bagging_date_range?.from ?? null;
  const satBaggingTo = sat?.verified_bagging_date_range?.to ?? null;
  const satBaggingCheck = sat?.bagging_date_check ?? 'NOT_AVAILABLE';

  const hasSatSection =
    satInitialLength !== null ||
    satCurrentLength !== null ||
    sat?.estimated_initial_weight_kg != null ||
    sat?.estimated_current_weight_kg != null ||
    satBaggingFrom !== null ||
    satBaggingTo !== null;

  // Textos secundarios: fecha de la imagen usada, fuente del largo inicial y banda del peso estimado.
  const satMeasuredAtCaption = (date?: string | null) =>
    date ? i18n.t('app.silobag_data.measured_at', { date: formatDate(date) }) : null;

  const satInitialSourceCaption =
    sat?.initial_length_source === 'BAGGING_EVENT'
      ? i18n.t('app.silobag_data.sat_source_bagging_event')
      : sat?.initial_length_source === 'NOT_AVAILABLE'
        ? i18n.t('app.silobag_data.sat_source_not_available')
        : null;

  const satInitialLengthCaption =
    [satMeasuredAtCaption(sat?.initial_length_date), satInitialSourceCaption].filter(Boolean).join(' · ') ||
    null;

  // TIC-4306: el peso estimado se muestra solo como rango [min, max]; el punto medio (`value`)
  // confundía a los usuarios, porque las reglas validan contra la banda y no contra ese valor.
  const satWeightBand = (band?: SatEstimatedWeightKg | null) =>
    formatWeightBand(toFiniteNumber(band?.min), toFiniteNumber(band?.max));

  const satWeightBandCaption = (band?: SatEstimatedWeightKg | null) => {
    const range = satWeightBand(band);
    return range ? i18n.t('app.silobag_data.estimated_weight_range', { range }) : null;
  };

  // --- Tooltips de los checks ---
  // Van sobre el DATO DECLARADO (largo, peso embolsado, stock y fecha de embolsado): son los campos
  // que el usuario cargó y puede corregir. Las celdas de valores medidos/estimados quedan como
  // referencia. `OK` explica el margen; `INCONSISTENT` dice la diferencia concreta.
  // TIC-3909-3: las reglas de peso validan la banda [min, max] de la especie SIN tolerancia, así que
  // el rango que se muestra en el caption y en el tooltip (OK e inconsistente) es exactamente el que
  // decide el check. El largo sí conserva su tolerancia (20 % / 6 m), por eso su OK habla de "margen".
  const satOkTooltip = i18n.t('app.silobag_data.sat_check_ok');

  const satLengthTooltip =
    satLengthCheck === 'OK'
      ? satOkTooltip
      : satLengthDiffPct !== null
        ? i18n.t('app.silobag_data.length_check_warning', { pct: satLengthDiffPct.toFixed(1) })
        : i18n.t('app.silobag_detail.warning_silosat');

  const satWeightTooltip = (
    check: SatCheck,
    diffPct: number | null,
    band?: SatEstimatedWeightKg | null
  ): string => {
    const caption = satWeightBandCaption(band);
    if (check === 'OK') {
      const ok = i18n.t('app.silobag_data.weight_check_ok');
      return caption ? `${ok} (${caption})` : ok;
    }
    const warning =
      diffPct !== null
        ? i18n.t('app.silobag_data.weight_check_warning', { pct: diffPct.toFixed(1) })
        : i18n.t('app.silobag_detail.warning_silosat');
    return caption ? `${warning} (${caption})` : warning;
  };

  // El check de "Stock campo" se muestra en dos lugares (SiloSat y Balance); el tooltip se arma una
  // sola vez para que ambos digan exactamente lo mismo.
  const satCurrentWeightTooltip = satWeightTooltip(
    satCurrentWeightCheck,
    satCurrentWeightDiffPct,
    sat?.estimated_current_weight_kg
  );

  // Se arma con el rango que se está mostrando, así el tooltip no puede contradecir a la celda.
  const satBaggingRangeText = `${satBaggingFrom ? formatDate(satBaggingFrom) : '―'} – ${
    satBaggingTo ? formatDate(satBaggingTo) : '―'
  }`;
  // Días de desvío del check inconsistente: los calcula el backend con signo y acá se muestran en
  // valor absoluto más la dirección. Si el campo no viene (backend anterior), queda `null` y el
  // tooltip es exactamente el de antes: solo el rango verificado.
  const satBaggingDiffText =
    satBaggingCheck === 'INCONSISTENT' ? formatBaggingDateDiff(sat?.bagging_date_diff_days) : null;
  const satBaggingTooltip =
    satBaggingCheck === 'OK'
      ? i18n.t('app.silobag_detail.certification_silosat', {
          firstVisibleDate: satBaggingFrom ? formatDate(satBaggingFrom) : '―',
          lastVisibleDate: satBaggingTo ? formatDate(satBaggingTo) : '―',
        })
      : satBaggingFrom || satBaggingTo
        ? [
            i18n.t('app.silobag_data.bagging_date_check_warning', { range: satBaggingRangeText }),
            satBaggingDiffText,
          ]
            .filter(Boolean)
            .join(' · ')
        : i18n.t('app.silobag_detail.different_date');

  return (
    <Paper
      elevation={0}
      sx={{
        textAlign: 'left',
        backgroundColor: 'white',
        borderRadius: '10px',
        padding: '20px',
      }}
    >
      {isLoading ? (
        <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
          <CircularProgress sx={{ color: 'green' }} size={60} />
        </div>
      ) : (
        <Box>
          {/* Condicionales de Modales */}
          {modalObservation && (
            <ObservationModal
              open={modalObservation}
              setOpen={setModalObservation}
              observations={data?.observations ?? ''}
            />
          )}
          {modalImage && <ImagesGalery open={modalImage} onClose={setModalImage} />}
          {modalFiles && (
            <FileModal
              open={modalFiles}
              setOpen={setModalFiles}
              data={files}
              deleteFile={deleteFile}
              writePermission={data?.write_permission}
            />
          )}
          {isClosed ? (
            <Box
              display={'flex'}
              justifyContent={'center'}
              mb={'2%'}
              style={{ backgroundColor: '#e4e4e4' }}
              borderRadius={'10px'}
            >
              <Typography color={'#7A7A7A'}>{i18n.t('app.silobag_detail.silo_close')}</Typography>
            </Box>
          ) : null}

          <Typography component="div" sx={textDisabledTablet}>
            {i18n.t('app.organization')}{' '}
            <span style={{ fontWeight: 600, fontSize: '15px', color: 'black' }}>
              {data?.organizations?.name}
            </span>
          </Typography>
          <Grid container spacing={5}>
            <Grid item xs={12} sm={10} md={10} mt={2}>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'start', md: 'center' }}
                justifyContent={'space-around'}
              >
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  width={{ xs: '100%', md: '40%' }}
                  mt={1}
                  alignItems={'start'}
                  display={'flex'}
                  justifyContent={'space-evenly'}
                >
                  <Typography component="div" sx={textDisabledTablet} display={'flex'} alignItems={'center'}>
                    <Typography component="span" mr={1} fontSize={'14px'}>
                      {i18n.t('app.silobag_data.register_date')}
                    </Typography>
                    <Typography
                      component="span"
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      fontSize={'16px'}
                    >
                      {created_at}
                    </Typography>
                  </Typography>
                  {/* ID */}
                  <Stack
                    sx={textDisabledTablet}
                    display={'flex'}
                    direction="row"
                    alignItems="center"
                    justifyContent="center"
                    spacing={1}
                    ml={{ xs: 0, md: 14 }}
                  >
                    <Typography mr={1} fontSize={'14px'}>
                      ID
                    </Typography>
                    <Typography color={'#49454F'} fontWeight={600} display={'block'} fontSize={'16px'}>
                      {data?.id}
                    </Typography>
                    <Tooltip title={i18n.t('app.copy') + ' ID'} placement="top">
                      <Button
                        sx={{ padding: '3px', minWidth: 30, minHeight: 25 }}
                        onClick={() => {
                          setIdCopied(false);
                          copyId(data?.id).then(() => {
                            setTimeout(() => {
                              setIdCopied(true);
                            }, 100);
                          });
                        }}
                      >
                        <img src={copyIcon} style={{ width: 16, height: 16 }} />
                      </Button>
                    </Tooltip>
                  </Stack>
                </Stack>
                <Stack
                  direction={{ xs: 'column', md: 'row' }}
                  width={{ xs: '100%', md: '90%' }}
                  display={'flex'}
                  justifyContent={'space-around'}
                  alignItems={'center'}
                  mt={1}
                >
                  {/* Establecimento */}
                  <Typography
                    component="div"
                    sx={{ ...textDisabledTablet, width: { xs: '100%', md: '60%' } }}
                    display={'flex'}
                    alignItems={'center'}
                  >
                    <Typography component="span" mr={1} fontSize={'14px'}>
                      {i18n.t('app.countryside')}
                    </Typography>
                    <Typography
                      component="span"
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      sx={{ xs: '118px', md: '100%' }}
                      fontSize={'16px'}
                    >
                      {countrysideName}
                    </Typography>
                  </Typography>
                  {/* Lote Stock */}
                  <Typography
                    component="div"
                    sx={textDisabledTablet}
                    width={{ xs: '100%', md: '28%' }}
                    display={'flex'}
                    justifyContent={'center'}
                    alignItems={'center'}
                    style={{ fontSize: '14px' }}
                  >
                    {i18n.t('app.silobag_data.lot_stock')}
                    <Typography
                      component="span"
                      style={{ color: '#49454F', fontWeight: 600, fontSize: '16px', marginLeft: '5px' }}
                    >
                      {data?.lots?.name}
                    </Typography>
                  </Typography>
                </Stack>
              </Stack>
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                display={'flex'}
                justifyContent={'space-between'}
                mt={3}
              >
                <Stack mb={3}>
                  {/* ESPECIE */}
                  <Stack direction={'row'} spacing={1} mb={3} alignItems="center">
                    <img
                      src={image_url}
                      alt={'image for silobag species type'}
                      style={{ width: 40, height: 40 }}
                    />
                    <Stack direction={{ xs: 'column' }}>
                      <Typography
                        color={'#49454F'}
                        fontWeight={600}
                        display={'block'}
                        width={'56px'}
                        fontSize={{ xs: '15px', md: '20px' }}
                      >
                        {speciesName}
                      </Typography>
                      <Typography sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                        {i18n.t('app.silobag_data.specie')}
                      </Typography>
                    </Stack>
                  </Stack>
                  {/* LARGO SILOBOLSA (embolsado - user input) */}
                  <Stack mb={3}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        color={'#49454F'}
                        fontWeight={600}
                        display={'block'}
                        fontSize={{ xs: '15px', md: '20px' }}
                      >
                        <Box component="span">{sizeClient ? `${sizeClient.toFixed(1)}m` : '―'}</Box>{' '}
                      </Typography>
                      {/* TIC-3909: el check del largo va sobre el largo DECLARADO, con el tooltip
                          que explica el margen (OK) o la diferencia concreta (INCONSISTENT). */}
                      {satLengthCheck === 'OK' && (
                        <Tooltip title={satLengthTooltip}>
                          <img src={NewReleaseIcon} alt="release-icon" />
                        </Tooltip>
                      )}
                      {satLengthCheck === 'INCONSISTENT' && (
                        <Tooltip title={satLengthTooltip}>
                          <Button
                            size="small"
                            sx={{ padding: '0px', minWidth: 30, minHeight: 25 }}
                            onClick={() => setSizeModal(true)}
                          >
                            <img src={warningIcon} alt="release-icon" />
                          </Button>
                        </Tooltip>
                      )}
                    </Stack>
                    <Typography sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                      {i18n.t('app.silobag_data.silobag_size')}
                    </Typography>
                  </Stack>
                </Stack>
                <Stack mb={3}>
                  {/* Producto  */}
                  <Typography component="div" sx={textDisabledTablet} mb={3} style={{ fontSize: '14px' }}>
                    <Typography
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      width={'56px'}
                      fontSize={{ xs: '15px', md: '20px' }}
                    >
                      {product_type || '―'}
                    </Typography>
                    <Typography component="span" sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                      {i18n.t('app.silobag_data.product')}{' '}
                    </Typography>
                  </Typography>
                  {/* PESO EMBOLSADO */}
                  <Typography component="div" sx={textDisabledTablet} mb={3}>
                    <Stack direction={'row'} spacing={7}>
                      <Typography
                        color={'#49454F'}
                        fontWeight={600}
                        display={'block'}
                        width={'56px'}
                        fontSize={{ xs: '15px', md: '20px' }}
                        style={{ whiteSpace: 'nowrap' }}
                      >
                        {data?.input_weight
                          ? i18n.numberToDelimited(parseFloat(data?.input_weight), {
                              delimiter: i18n.t('config.delimiter'),
                            }) + ' kg'
                          : '―'}
                      </Typography>
                      {data?.is_corvus_weight && (
                        <Tooltip
                          title={
                            <div>
                              {i18n.t('app.integration_corvus.corvus_weight_certified')}
                              <br />
                              {i18n.t('app.integration_corvus.bagging_weight_original')}{' '}
                              {data?.weight_before_corvus} kg
                            </div>
                          }
                        >
                          <img src={NewReleaseIcon} alt="release-icon" />
                        </Tooltip>
                      )}
                    </Stack>
                    {i18n.t('app.silobag_data.weight_bagging')}{' '}
                  </Typography>
                </Stack>
                <Stack mb={3} marginLeft={{ md: '50px' }}>
                  {/* VARIEDAD */}
                  <Typography component="div" sx={textDisabledTablet} style={{ fontSize: '14px' }} mb={3}>
                    <Typography
                      component="span"
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      width={'80px'}
                      fontSize={{ xs: '15px', md: '20px' }}
                    >
                      {data?.variety || '―'}
                    </Typography>
                    {i18n.t('app.silobag_data.variety')}{' '}
                  </Typography>
                  {/* HUMEDAD INICIAL */}
                  <Typography component="div" sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                    <Typography
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      width={'100px'}
                      fontSize={{ xs: '15px', md: '20px' }}
                    >
                      {data?.initial_humidity || '―'} %
                    </Typography>
                    {i18n.t('app.silobag_data.humidity_bagging')}
                  </Typography>
                </Stack>
                <Stack mb={3}>
                  {/* COSECHA */}
                  <Typography component="div" sx={textDisabledTablet} style={{ fontSize: '14px' }} mb={3}>
                    <Typography
                      component="span"
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      width={'100px'}
                      fontSize={{ xs: '15px', md: '20px' }}
                    >
                      {data?.silobag_harvests?.name || '―'}
                    </Typography>
                    {i18n.t('app.silobag_data.harvest')}{' '}
                  </Typography>
                  {/* TAS */}
                  <Typography component="div" sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                    <Typography
                      component="span"
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      width={'100px'}
                      fontSize={{ xs: '15px', md: '20px' }}
                    >
                      <span style={{ fontSize: 18 }}>{TAS}</span>
                    </Typography>

                    <span
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      TAS{' '}
                      <Tooltip title={i18n.t('app.silobag_detail.tas_information')}>
                        <IconButton
                          size="small"
                          sx={{
                            padding: '2px',
                            marginLeft: '2px',
                          }}
                        >
                          <img
                            src={HelpIcon}
                            alt="tas-help"
                            style={{
                              width: 16,
                              height: 16,
                            }}
                          />
                        </IconButton>
                      </Tooltip>
                    </span>
                  </Typography>
                </Stack>
                <Stack mb={3}>
                  {/* FECHA EMBOLSADO */}
                  <Stack mb={3}>
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Typography
                        component="div"
                        color={'#49454F'}
                        fontWeight={600}
                        display={'block'}
                        fontSize={{ xs: '15px', md: '20px' }}
                      >
                        <Box component="span">{dateClient ? formatDate(dateClient) : '―'}</Box>
                      </Typography>
                      {/* TIC-3909: el check de la fecha declarada sale de `sat_measurements`
                          (rango del evento de embolsado), no de `v_silobags_bagging_date_check`. */}
                      {satBaggingCheck === 'OK' ? (
                        <Tooltip title={satBaggingTooltip}>
                          <Box>
                            <img src={NewReleaseIcon} alt="release-icon" />
                          </Box>
                        </Tooltip>
                      ) : satBaggingCheck === 'INCONSISTENT' ? (
                        <Tooltip title={satBaggingTooltip}>
                          <Button
                            size="small"
                            sx={{ padding: '0px', minWidth: 30, minHeight: 25 }}
                            onClick={() => {
                              setOpenModal(true);
                            }}
                          >
                            <img src={warningIcon} alt="release-icon" />
                          </Button>
                        </Tooltip>
                      ) : null}
                    </Stack>
                    <Typography sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                      {i18n.t('app.silobag_data.bagging_date2')}{' '}
                    </Typography>
                  </Stack>
                  {/* DESDE EMBOLSADO */}
                  <Typography
                    sx={textDisabledTablet}
                    mb={3}
                    style={{ fontSize: '14px' }}
                    marginRight={{ md: '40px' }}
                  >
                    <Typography
                      component="span"
                      color={'#49454F'}
                      fontWeight={600}
                      display={'block'}
                      width={'100px'}
                      fontSize={{ xs: '15px', md: '20px' }}
                    >
                      {daysToShow !== '-' ? `${daysToShow} días` : '―'}
                    </Typography>
                    {i18n.t('app.silobag_data.bagging_from')}
                  </Typography>
                </Stack>
              </Stack>
              {/* --- TIC-3909: datos que usan las inconsistencias v2 (bloque `sat_measurements`) --- */}
              {hasSatSection && (
                <Box
                  sx={{
                    mt: 1,
                    mb: 2,
                    py: 1.5,
                    px: 2,
                    borderRadius: '8px',
                    backgroundColor: '#F5F7FA',
                    border: '1px solid #E0E3E8',
                  }}
                >
                  <Typography sx={{ fontWeight: 600, fontSize: '14px', mb: 1.5 }}>
                    {i18n.t('app.silobag_data.sat_verified_section')}
                  </Typography>

                  {/* 1. LARGOS: declarado (con el check) vs inicial verificado vs actual verificado.
                      El ícono va SIEMPRE en el dato declarado: es el campo que el usuario cargó y
                      puede corregir. Las celdas verificadas quedan como referencia (fecha de la
                      imagen y su id en el tooltip), sin duplicar el ícono. */}
                  <Grid container spacing={2}>
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.declared_size')}
                        value={formatMeters(sizeClient || null)}
                        check={satLengthCheck}
                        checkTooltip={satLengthTooltip}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.initial_verified_size')}
                        value={formatMeters(satInitialLength)}
                        caption={satInitialLengthCaption}
                        traceTooltip={
                          sat?.initial_sat_view_id != null
                            ? i18n.t('app.silobag_data.sat_image_id', { id: sat.initial_sat_view_id })
                            : undefined
                        }
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.current_verified_size')}
                        value={formatMeters(satCurrentLength)}
                        caption={satMeasuredAtCaption(sat?.current_length_date)}
                        traceTooltip={
                          sat?.current_sat_view_id != null
                            ? i18n.t('app.silobag_data.sat_image_id', { id: sat.current_sat_view_id })
                            : undefined
                        }
                      />
                    </Grid>
                    {showSatDiameter && (
                      <Grid item xs={12} sm={6} md={3}>
                        <SatField
                          label={i18n.t('app.silobag_data.diameter')}
                          value={`${satDiameterFt}`}
                          caption={i18n.t('app.silobag_data.sat_diameter_factor', {
                            factor: satFactor?.toFixed(2),
                          })}
                        />
                      </Grid>
                    )}

                    {/* 2. PESOS: embolsado declarado vs estimado inicial, stock vs estimado actual.
                        Los checks van en los declarados (peso embolsado y stock actual). */}
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.weight_bagging')}
                        value={formatKilograms(declaredWeight)}
                        check={satInitialWeightCheck}
                        checkTooltip={satWeightTooltip(
                          satInitialWeightCheck,
                          satInitialWeightDiffPct,
                          sat?.estimated_initial_weight_kg
                        )}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.estimated_initial_weight')}
                        value={satWeightBand(sat?.estimated_initial_weight_kg)}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.remaining_stock')}
                        value={formatKilograms(remainingStock)}
                        check={satCurrentWeightCheck}
                        checkTooltip={satCurrentWeightTooltip}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.estimated_current_weight')}
                        value={satWeightBand(sat?.estimated_current_weight_kg)}
                      />
                    </Grid>

                    {/* 3. FECHA DE EMBOLSADO: declarada (con el check) vs rango verificado.
                        El ícono va solo en la declarada; el rango es la referencia que explica el
                        resultado y ya viaja en el tooltip del check. */}
                    <Grid item xs={12} sm={6} md={3}>
                      <SatField
                        label={i18n.t('app.silobag_data.bagging_date2')}
                        value={
                          (sat?.declared_bagging_date ?? dateClient)
                            ? formatDate(sat?.declared_bagging_date ?? dateClient)
                            : null
                        }
                        check={satBaggingCheck}
                        checkTooltip={satBaggingTooltip}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <SatField
                        label={i18n.t('app.silobag_data.verified_bagging_date')}
                        value={satBaggingFrom && satBaggingTo ? satBaggingRangeText : null}
                        traceTooltip={
                          sat?.verified_bagging_date_range?.before_sat_view_id != null ||
                          sat?.verified_bagging_date_range?.after_sat_view_id != null
                            ? i18n.t('app.silobag_data.sat_image_range_ids', {
                                before:
                                  sat?.verified_bagging_date_range?.before_sat_view_id ??
                                  i18n.t('app.silobag_data.not_available'),
                                after:
                                  sat?.verified_bagging_date_range?.after_sat_view_id ??
                                  i18n.t('app.silobag_data.not_available'),
                              })
                            : undefined
                        }
                      />
                    </Grid>
                  </Grid>
                </Box>
              )}
              {/* ORIGIN LOTS */}
              <Stack
                direction={{ xs: 'column', md: 'row' }}
                alignItems={{ xs: 'start', md: 'center' }}
                flexWrap={'wrap'}
                display={'flex'}
              >
                {originLots?.map((originLot) => (
                  <Stack mr={'30px'} direction={'row'} alignItems={'center'} key={originLot.id}>
                    <Typography sx={textDisabledTablet} style={{ fontSize: '14px' }}>
                      {i18n.t('app.silobag_data.lot_origin')}
                    </Typography>
                    <Typography
                      key={originLot.lots.id}
                      style={{ marginRight: '5px', fontWeight: 600, marginLeft: '5px' }}
                    >
                      {originLot.lots.name}
                    </Typography>
                    <Typography sx={textDisabledTablet} style={{ fontSize: '14px', marginLeft: '10px' }}>
                      Peso {i18n.t('app.silobag_data.lot_origin')}{' '}
                      <span style={{ fontWeight: 600, fontSize: '16px', color: 'black' }}>
                        {originLot.weight || '―'} kg
                      </span>
                    </Typography>
                  </Stack>
                ))}
              </Stack>
            </Grid>
            {/* Acciones */}
            <Grid item xs={12} sm={2} marginTop={{ xs: '20px', md: '-10px' }} md={2}>
              <Typography component="div" sx={{ marginTop: '5px' }}>
                <Button
                  // DisabledMapIcon
                  startIcon={
                    <img src={isDisabledSB ? DisabledMapIcon : MapIcon} style={{ width: 16, height: 13 }} />
                  }
                  variant="text"
                  sx={buttonDataDetail}
                  disabled={isDisabledSB}
                  onClick={() => navigate(`${PATH.MAP}/${data.id}`)}
                >
                  {i18n.t('app.silobag_detail.view_in_map')}
                </Button>
              </Typography>
              <Button
                startIcon={
                  <img
                    src={imagesSilo?.length > 0 ? imgIcon : imgDisabledIcon}
                    style={{ width: 16, height: 13 }}
                  />
                }
                variant="text"
                disabled={imagesSilo.length <= 0 ? true : false}
                sx={buttonDataDetail}
                onClick={handleModalImageOpen}
              >
                {i18n.t('app.silobag_detail.galery_images')}(
                {isSbImgLoading ? (
                  <CircularProgress size={12} thickness={8} sx={{ color: '#09AAEB' }} />
                ) : (
                  totalImages
                )}
                )
              </Button>
              <Tooltip
                title={i18n.t('app.silobag_detail.files_tooltip')}
                disableHoverListener={files.length > 0}
              >
                <div>
                  <Button
                    startIcon={
                      <img
                        src={files?.length > 0 ? FileIcon : FileDisabledIcon}
                        style={{ width: 16, height: 13 }}
                      />
                    }
                    variant="text"
                    disabled={files?.length > 0 ? false : true}
                    sx={buttonDataDetail}
                    onClick={handleModalFilesOpen}
                  >
                    {i18n.t('app.silobag_detail.view_files')}
                    {files?.length > 0 ? `(${files?.length})` : ''}
                  </Button>
                </div>
              </Tooltip>
              <Tooltip
                title={i18n.t('app.silobag_detail.observation_tooltip')}
                disableHoverListener={data?.observations !== null && data?.observations.length > 0}
              >
                <Box>
                  <Button
                    startIcon={
                      <img
                        src={data?.observations === '' ? ObservationDisabledIcon : ObservationIcon}
                        style={{ width: 16, height: 13 }}
                      />
                    }
                    variant="text"
                    disabled={data?.observations === ''}
                    sx={buttonDataDetail}
                    onClick={handleModalObervationOpen}
                  >
                    {i18n.t('app.observations')}
                  </Button>
                </Box>
              </Tooltip>
              <Typography component="div">
                <Button
                  startIcon={
                    <img
                      src={data?.write_permission === false ? GreyEditIcon : EditIcon}
                      style={{ width: 16, height: 13 }}
                    />
                  }
                  variant="text"
                  sx={buttonDataDetail}
                  onClick={() =>
                    navigate(`${PATH.SILOBAG}/${data?.id}/edit`, {
                      state: {
                        from: `${PATH.SILOBAG}/${data?.id}`,
                        isGuest,
                        isCorvus: data?.is_corvus_weight,
                        siloListId: siloListId,
                      },
                    })
                  }
                  disabled={data?.write_permission === false}
                >
                  Editar
                </Button>
              </Typography>
              <Button
                startIcon={<img src={ReloadIcon} style={{ width: 16, height: 13 }} />}
                variant="text"
                sx={buttonDataDetail}
                onClick={() => location.reload()}
              >
                {i18n.t('app.silobag_detail.refresh')}
              </Button>
            </Grid>
          </Grid>
          {/* DATOS ADICIONALES */}
          <Accordion
            disableGutters
            slotProps={{ heading: { component: 'h4' } }}
            sx={{
              boxShadow: 'none',
              backgroundColor: 'transparent',
              marginTop: '20px',
              '&:before': { height: '0px' },
            }}
          >
            <AccordionSummary
              expandIcon={<img src={ChevronDown} style={{ width: 10, height: 10 }} />}
              style={{ width: 'max-content' }}
            >
              <Typography
                sx={{ fontWeight: '600', fontSize: '14px', marginLeft: '-10px', marginRight: '10px' }}
              >
                {i18n.t('app.silobag_data.additional_data')} <span> </span>
              </Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ maxHeight: '250px', overflow: 'auto' }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={3}>
                  <Typography sx={{ fontWeight: 600, fontSize: '18px', color: 'black' }}>
                    {data?.responsible_for_bagging || '―'}
                  </Typography>
                  <Typography sx={textDisabledTablet}>
                    {i18n.t('app.silobag_data.bagging_responsible')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.silobag_brand || '―'}
                    </span>
                    % {i18n.t('app.silobag_data.silobag_brand')}
                  </Typography>
                </Grid>

                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.varietal_purity || '―'}
                    </span>
                    {i18n.t('app.silobag_data.varietal_purity')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.fillying_coefficient || '―'}
                    </span>
                    {i18n.t('app.silobag_data.fillying_coefficient')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.humidity_sources?.humidity_sources_translations?.[0]?.description || '―'}
                    </span>
                    {i18n.t('app.silobag_data.source_humidity')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.split_grain_percentage || '―'} %
                    </span>
                    {i18n.t('app.silobag_data.defective')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.weight_sources?.weight_sources_translations?.[0]?.description || '―'}
                    </span>
                    {i18n.t('app.silobag_data.source_weight')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.side_charge?.side_charge_translations?.[0]?.description || '―'}
                    </span>
                    {i18n.t('app.silobag_data.side_charge')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      ―
                    </span>
                    {i18n.t('app.silobag_data.have_breaks')}
                  </Typography>
                </Grid>
                <Grid item xs={12} sm={3}>
                  <Typography sx={textDisabledTablet}>
                    <span style={{ fontWeight: 600, fontSize: '18px', color: 'black', display: 'block' }}>
                      {data?.diameter ?? null}
                    </span>
                    {i18n.t('app.silobag_data.diameter')}
                  </Typography>
                </Grid>
              </Grid>
            </AccordionDetails>
          </Accordion>
          {/* Balance */}
          {((data?.silobag_extractions && data?.silobag_extractions.length > 0) || !data?.active) && (
            <>
              <Box sx={{ my: 2, height: '50px', borderBottom: 1, borderColor: 'grey.500' }} />
              <Grid item xs={12} sm={12} sx={{ marginTop: '-20px' }}>
                <Grid container spacing={2}>
                  <Grid item xs={12}>
                    <Typography sx={{ fontWeight: '600', fontSize: '1.2rem' }}>Balance</Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography sx={textDisabledTablet} style={{ marginTop: '30px' }}>
                      <span style={{ fontWeight: 600, fontSize: '15px', color: 'black', display: 'block' }}>
                        {(data?.total_extracted_weight || '―').toLocaleString().replace(/,/g, '.') + ' kg'}
                      </span>
                      {i18n.t('app.silobag_detail.weight_extractions')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography component="div" sx={textDisabledTablet} style={{ marginTop: '30px' }}>
                      {/* Mismo check que el "Stock campo" de la sección SiloSat: el stock declarado
                          contra el peso estimado por el largo medido. Se repite acá porque es donde
                          se mira el balance de extracciones. */}
                      <Stack direction="row" spacing={1} alignItems="center">
                        <span style={{ fontWeight: 600, fontSize: '15px', color: 'black', display: 'block' }}>
                          {(data?.remaining_stock || '0')?.toLocaleString().replace(/,/g, '.') + ' kg'}
                        </span>
                        {satCurrentWeightCheck === 'INCONSISTENT' && (
                          <Tooltip title={satCurrentWeightTooltip}>
                            <Box component="span" display="flex">
                              <img src={warningIcon} alt="warning-icon" />
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                      {i18n.t('app.silobag_data.remaining_stock')}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography component="div" sx={textDisabledTablet} style={{ marginTop: '30px' }}>
                      <span style={{ fontWeight: 600, fontSize: '15px', color: 'black', display: 'block' }}>
                        {!isClosed
                          ? '0 kg'
                          : //TODO para revisar el ? del data  y revisar la conversion a int de total_extracted_weight
                            ((+data?.total_extracted_weight || 0) - (+data?.input_weight || 0))
                              .toLocaleString('en-US')
                              .replace(/,/g, '.') + ' kg'}
                      </span>
                      {i18n.t('app.silobag_detail.difference')}
                      {!isClosed ? null : (
                        <Chip
                          //TODO para revisar el ? del data  y revisar la conversion a int de total_extracted_weight
                          label={
                            parseFloat(data?.input_weight) > parseFloat(data?.total_extracted_weight)
                              ? 'Deficit'
                              : 'Superávit'
                          }
                          sx={{
                            //TODO para revisar el ? del data  y revisar la conversion a int de total_extracted_weight
                            backgroundColor:
                              parseFloat(data?.input_weight) > parseFloat(data?.total_extracted_weight)
                                ? 'red'
                                : 'green',
                            color: 'white',
                            fontSize: '9px',
                            fontWeight: 'bold',
                            padding: '2px -15px',
                            height: '20px',
                            width: '80px',
                            marginLeft: '5px',
                          }}
                        />
                      )}
                    </Typography>
                  </Grid>
                  <Grid item xs={12} sm={3}>
                    <Typography component="div" sx={textDisabledTablet} style={{ marginTop: '30px' }}>
                      <span
                        style={{
                          fontWeight: 600,
                          fontSize: '15px',
                          color: 'black',
                          display: 'block',
                        }}
                      >
                        {!isClosed
                          ? '0.00' + '%'
                          : // TODO: para revisar el ? del data  y no es necesario el nullish coalescing porque el parsefloat no devuele null
                            (
                              (((parseFloat(data?.total_extracted_weight) ?? 0) -
                                (parseFloat(data?.input_weight) ?? 0)) /
                                (parseFloat(data?.input_weight) ?? 1)) *
                              100
                            ).toFixed(2) + '%'}
                      </span>
                      {i18n.t('app.silobag_detail.difference')} en %
                    </Typography>
                  </Grid>
                </Grid>
              </Grid>
            </>
          )}
        </Box>
      )}
      {isSuccess && (
        <ToastAction message={i18n.t('app.silobag_detail.file_deleted_toast')} severity="success" />
      )}
      {idCopied && (
        <ToastAction
          message={i18n.t('app.silobag_detail.id_copied_toast')}
          severity="success"
          onClose={() => setIdCopied(false)}
        />
      )}
      <MyModal open={openModal} onClose={handleCloseModal}>
        <ModalContent
          dateToShow={dateToShow!}
          silobagStatus={data?.active}
          selectedDate={selectedDate as Dayjs}
          setSelectedDate={setSelectedDate}
          handleClose={handleClose}
          handleUpdateDate={handleUpdateDate}
          startDate={satBaggingFrom}
          endDate={satBaggingTo}
        />
      </MyModal>
      <SizeModal open={sizeModal} onClose={handleCloseSizeModal}>
        <Box component={'div'}>
          <Typography variant="h5">{i18n.t('app.silobag_detail.different_size')}</Typography>
        </Box>
        <Box component={'div'}>
          <Typography variant="body1">{i18n.t('app.silobag_detail.different_context_size')}</Typography>
        </Box>
        <Grid container spacing={3}>
          <Grid item xs={6}>
            <Typography variant="body1">{i18n.t('app.silobag_detail.silosat_size')}</Typography>
            <Typography variant="body1">
              {formatMeters(satInitialLength) ?? i18n.t('app.silobag_data.not_available')}
            </Typography>
          </Grid>
          <Grid item xs={6}>
            <TextField
              label={i18n.t('app.silobag_data.silobag_size')}
              value={selectSize}
              onChange={(e) => SetSelectSize(e.target.value)}
            />
          </Grid>
        </Grid>

        <Box width={'100%'} display={'flex'} justifyContent={'end'} component={'div'}>
          <Box width="100%" display="flex" justifyContent="flex-end">
            <Button variant="text" onClick={handleCloseSizeModal}>
              {i18n.t('app.close')}
            </Button>
            <Button sx={configButtonStylesContained} variant="contained" onClick={handleSizeChange}>
              {i18n.t('app.save')}
            </Button>
          </Box>
        </Box>
      </SizeModal>
    </Paper>
  );
}

type ModalProps = {
  open: boolean;
  onClose: () => void;
};

export function MyModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '90%',
    maxWidth: '750px',
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style} component={'div'}>
          {children}
        </Box>
      </Fade>
    </Modal>
  );
}

function SizeModal({ open, onClose, children }: PropsWithChildren<ModalProps>) {
  const style = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%)',
    width: '70%',
    maxWidth: 600,
    maxHeight: '95vh',
    bgcolor: 'background.paper',
    borderRadius: 4,
    boxShadow: 24,
    p: 2,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
  };

  const handleClose = (_: never, reason: string) => {
    if (reason === 'backdropClick') return;
    onClose();
  };

  return (
    <Modal open={open} keepMounted onClose={handleClose}>
      <Fade in={open} appear={false} timeout={175}>
        <Box sx={style} component={'div'}>
          {children}
        </Box>
      </Fade>
    </Modal>
  );
}
